/**
 * Serviço responsável por abrir arquivos no CorelDraw para produção
 * @module AbrirArquivoService
 */

import { corelCommunication } from '../communication';
import { WebViewResponse } from '../types/messages';

export interface AbrirArquivoOptions {
  /** Exibir mensagens de erro no Corel */
  mostrarErros: boolean;
  /** Manter estrutura de camadas ao abrir */
  manterCamadas: boolean;
  /** Importar estilos do arquivo */
  importarEstilos: boolean;
  /** Manter aparência visual do arquivo */
  manterAparencia: boolean;
  /** Abrir em modo visualização (somente leitura) */
  modoVisualizacao: boolean;
  /** Fechar documentos existentes antes de abrir */
  fecharDocumentosExistentes: boolean;
  /** Aguardar conclusão da abertura antes de responder */
  aguardarConclusao: boolean;
  /** Timeout em segundos para a operação */
  timeoutSegundos: number;
  /** Validar existência do arquivo e acessibilidade */
  validarArquivo: boolean;
  /** Verificar conectividade de rede antes de abrir */
  verificarConectividadeRede: boolean;
}

export interface SmbCredentials {
  username: string;
  password: string;
  /** Domínio SMB (opcional) */
  domain?: string | null;
}

export interface AbrirArquivoData {
  /** Caminho completo do arquivo a ser aberto */
  caminhoArquivo: string;
  /** Hash esperado para validação de integridade (quando habilitada) */
  hashEsperado?: string;
  /** Ativa verificação de integridade via hash */
  validarIntegridade?: boolean;
  /** Opções para abertura */
  opcoes: AbrirArquivoOptions;
  /** Credenciais SMB para acesso a compartilhamentos de rede */
  smb?: SmbCredentials;
}

/**
 * Serviço para envio de comandos de abertura de arquivo ao WebView/Corel.
 * Implementa métodos de conveniência com defaults de produção e variações com validação e credenciais SMB.
 */
export class AbrirArquivoService {
  private static instance: AbrirArquivoService;

  // Opções fixas para produção
  private static readonly OPCOES_PRODUCAO: AbrirArquivoOptions = {
    mostrarErros: false,
    manterCamadas: true,
    importarEstilos: false,
    manterAparencia: true,
    modoVisualizacao: false,
    fecharDocumentosExistentes: false,
    aguardarConclusao: true,
    timeoutSegundos: 1200, // 20 minutos (consistente com frontend)
    validarArquivo: true,
    verificarConectividadeRede: true
  };

  private constructor() {}

  /**
   * Obtém a instância única do serviço (singleton)
   */
  public static getInstance(): AbrirArquivoService {
    if (!this.instance) {
      this.instance = new AbrirArquivoService();
    }
    return this.instance;
  }

  /**
   * Abre um arquivo no CorelDraw com as opções padrão de produção
   * @param caminhoArquivo Caminho completo do arquivo a ser aberto
   * @param callback Função opcional para tratar a resposta
   * @returns ID da requisição ou null se falhar
   */
  public abrirArquivo(
    caminhoArquivo: string, 
    callback?: (response: WebViewResponse) => void
  ): string | null {
    const data: AbrirArquivoData = {
      caminhoArquivo,
      validarIntegridade: false,
      opcoes: AbrirArquivoService.OPCOES_PRODUCAO,
      // idempotência: abrir arquivo para o mesmo caminho em janela curta
      ...(caminhoArquivo ? { idempotencyKey: `open|${caminhoArquivo}` } as any : {})
    };

    // Passa callback direto no sendActionGuarded (com validação HMAC)
    return corelCommunication.sendActionGuarded('abrir-arquivo', data, callback);
  }

  /**
   * Abre um arquivo no CorelDraw com opções customizadas
   * @param caminhoArquivo Caminho completo do arquivo a ser aberto
   * @param opcoes Opções customizadas para abertura do arquivo
   * @param callback Função opcional para tratar a resposta
   * @returns ID da requisição ou null se falhar
   */
  public abrirArquivoComOpcoes(
    caminhoArquivo: string,
    opcoes: Partial<AbrirArquivoOptions> & { hashEsperado?: string; validarIntegridade?: boolean; smb?: SmbCredentials },
    callback?: (response: WebViewResponse) => void
  ): string | null {
    const data: AbrirArquivoData = {
      caminhoArquivo,
      hashEsperado: opcoes.hashEsperado,
      validarIntegridade: opcoes.validarIntegridade,
      opcoes: { ...AbrirArquivoService.OPCOES_PRODUCAO, ...opcoes },
      smb: opcoes.smb
    };
    (data as any).idempotencyKey = `open|${caminhoArquivo}|${opcoes.hashEsperado || 'no-hash'}`;

    // Passa callback direto no sendActionGuarded (com validação HMAC)
    return corelCommunication.sendActionGuarded('abrir-arquivo', data, callback);
  }

  /**
   * Abre arquivo com validação de integridade opcional (hash esperado)
   * @param caminhoArquivo Caminho completo do arquivo a ser aberto
   * @param params Parâmetros adicionais: hash esperado, flag de validação, opções parciais e credenciais SMB
   * @param callback Função opcional para tratar a resposta
   * @returns ID da requisição ou null se falhar
   */
  public abrirArquivoComValidacao(
    caminhoArquivo: string,
    params: { hashEsperado?: string; validarIntegridade?: boolean; opcoes?: Partial<AbrirArquivoOptions>; smb?: SmbCredentials },
    callback?: (response: WebViewResponse) => void
  ): string | null {
    const data: AbrirArquivoData = {
      caminhoArquivo,
      hashEsperado: params.hashEsperado,
      validarIntegridade: params.validarIntegridade ?? false,
      opcoes: { ...AbrirArquivoService.OPCOES_PRODUCAO, ...(params.opcoes || {}) },
      smb: params.smb
    };
    (data as any).idempotencyKey = `open|${caminhoArquivo}|${params.hashEsperado || 'no-hash'}`;

    // Passa callback direto no sendActionGuarded (com validação HMAC)
    return corelCommunication.sendActionGuarded('abrir-arquivo', data, callback);
  }

  /**
   * Abre arquivo com credenciais SMB explícitas
   * @param caminhoArquivo Caminho completo do arquivo a ser aberto
   * @param smb Credenciais SMB para autenticação (opcional)
   * @param callback Função opcional para tratar a resposta
   * @returns ID da requisição ou null se falhar
   */
  public abrirArquivoComCredenciais(
    caminhoArquivo: string,
    smb: SmbCredentials | undefined,
    callback?: (response: WebViewResponse) => void
  ): string | null {
    const data: AbrirArquivoData = {
      caminhoArquivo,
      validarIntegridade: false,
      opcoes: { ...AbrirArquivoService.OPCOES_PRODUCAO },
      ...(smb ? { smb } : {})
    };
    (data as any).idempotencyKey = `open|${caminhoArquivo}`;
    
    // Passa callback direto no sendActionGuarded (com validação HMAC)
    return corelCommunication.sendActionGuarded('abrir-arquivo', data, callback);
  }

  /**
   * Obtém as opções padrão de produção
   */
  public static getOpcoesProducao(): AbrirArquivoOptions {
    return { ...this.OPCOES_PRODUCAO };
  }
}

// Exporta a instância única
/** Instância singleton do serviço de abertura de arquivo. */
export const abrirArquivoService = AbrirArquivoService.getInstance();

// Função de conveniência para uso direto
/**
 * Atalho para abrir arquivo com defaults de produção.
 * @param caminhoArquivo Caminho completo do arquivo CDR
 * @param callback Callback opcional para resposta do WebView
 */
export const abrirArquivo = (caminhoArquivo: string, callback?: (response: WebViewResponse) => void): string | null => {
  return abrirArquivoService.abrirArquivo(caminhoArquivo, callback);
}; 

/**
 * Atalho para abrir arquivo com validação de integridade e/ou opções parciais.
 */
export const abrirArquivoComValidacao = (
  caminhoArquivo: string,
  params: { hashEsperado?: string; validarIntegridade?: boolean; opcoes?: Partial<AbrirArquivoOptions>; smb?: SmbCredentials },
  callback?: (response: WebViewResponse) => void
): string | null => {
  return abrirArquivoService.abrirArquivoComValidacao(caminhoArquivo, params, callback);
};

/**
 * Atalho para abrir arquivo usando credenciais SMB.
 */
export const abrirArquivoComCredenciais = (
  caminhoArquivo: string,
  smb: SmbCredentials | undefined,
  callback?: (response: WebViewResponse) => void
): string | null => {
  return abrirArquivoService.abrirArquivoComCredenciais(caminhoArquivo, smb, callback);
};